import { MilestoneKey } from "../enums/Status";
import {
  EngagementCategory,
  EngagementRecord,
} from "../models/stewardshipModels";
import { MILESTONE_DESCRIPTIONS } from "../pages/CompanyProfile/constants";
import { isNotNan } from "./formattingUtils";

const MILESTONE_COLUMN_SUFFIX = {
  Start: "_start",
  Status: "_status",
} as const;

const normalizeMilestoneValue = (
  value?: string | null,
): MilestoneKey | null => {
  if (!value) return null;

  const normalizedValue = value.trim().toUpperCase();
  if (
    [
      MilestoneKey.M1,
      MilestoneKey.M2,
      MilestoneKey.M3,
      MilestoneKey.M4,
      MilestoneKey.CI,
    ].includes(normalizedValue as MilestoneKey)
  ) {
    return normalizedValue as MilestoneKey;
  }

  return null;
};

export const isCorporateIssueEngagement = (
  engagementCategory?: string | null,
): boolean => engagementCategory === EngagementCategory.Issue;

export const getMilestoneSelectOptions = (isCorporateIssue: boolean) => {
  if (isCorporateIssue) {
    return [
      {
        value: MilestoneKey.CI,
        label: `${MilestoneKey.CI}: ${MILESTONE_DESCRIPTIONS[MilestoneKey.CI]}`,
      },
    ];
  }

  return [
    MilestoneKey.M1,
    MilestoneKey.M2,
    MilestoneKey.M3,
    MilestoneKey.M4,
  ].map((key) => ({
    value: key,
    label: `${key}: ${MILESTONE_DESCRIPTIONS[key]}`,
  }));
};

const normalizeApiMilestoneValue = (
  value?: string | null,
): MilestoneKey | null => {
  if (!value) return null;

  const normalizedValue = value.trim().toLowerCase();
  if (normalizedValue === "corporate_issue") return MilestoneKey.CI;
  if (normalizedValue === "finished") return MilestoneKey.M4;

  return normalizeMilestoneValue(value);
};

export const resolveEngagementMilestone = (
  engagementRecord: Partial<EngagementRecord> & {
    milestone?: string | null;
    current_milestone?: string | null;
  },
): MilestoneKey => {
  const explicitMilestone = normalizeMilestoneValue(engagementRecord.milestone);
  if (explicitMilestone) return explicitMilestone;

  const apiMilestone = normalizeApiMilestoneValue(
    engagementRecord.current_milestone,
  );
  if (apiMilestone) return apiMilestone;

  const milestones = Object.values(MilestoneKey) as MilestoneKey[];
  let activeMilestone: MilestoneKey = MilestoneKey.M1;

  for (const milestone of milestones) {
    const startKey =
      `${milestone.toLowerCase()}${MILESTONE_COLUMN_SUFFIX.Start}` as keyof EngagementRecord;
    const statusKey =
      `${milestone.toLowerCase()}${MILESTONE_COLUMN_SUFFIX.Status}` as keyof EngagementRecord;

    const startDate = engagementRecord[startKey] as string | undefined;
    const status = engagementRecord[statusKey] as string | undefined;

    if (isNotNan(startDate) || isNotNan(status)) {
      activeMilestone = milestone;
    }
  }

  return activeMilestone;
};
